import {
  IContactFormData,
  SuiteListItemContactForm,
} from 'src/SharedCustomHooks/UseContactFormData/types';
import { useState } from 'react';
import { getLocations } from 'src/services/client';
import {
  ERROR_MESSAGES,
  VALIDATION_RULES,
} from 'src/SharedCustomHooks/UseContactFormData/contactFormValidation';
import { IUserSuite } from 'src/services/api/ServiceRequestDashboard/apiContracts';

interface UseContactFormDataState {
  contactFormData: IContactFormData;
  isLoading: boolean;
  errors: Record<string, string>;
  location: any;
  predictions: any[];
}

export interface UseContactFormDataReturn {
  // State
  contactFormData: IContactFormData;
  isLoading: boolean;
  errors: Record<string, string>;
  location: string;
  predictions: Array<{ description: string; [key: string]: any }>;

  // Form State Actions
  setContactFormData: (data: Partial<IContactFormData>) => void;
  updateContactFormData: (updates: Partial<IContactFormData>) => void;
  clearContactFormData: () => void;
  initializeContactFormDataFromEdit: (
    editData: Partial<IContactFormData>
  ) => void;

  // Error Management
  setErrors: (newErrors: Record<string, string>) => void;
  clearError: (field: string) => void;

  // Loading State
  setLoading: (loading: boolean) => void;

  // Suite Management
  clearSuites: () => void;
  clearSuitesInFormForRemovedProperty: (selectedPropertyToRemove: any) => void;

  // Location Search Actions
  setLocation: (location: string) => void;
  setPredictions: (
    predictions: Array<{ description: string; [key: string]: any }>
  ) => void;
  clearLocationSearch: () => void;
  getLocation: (value: string) => Promise<void>;
  onClickLocation: (address: string) => Promise<void>;

  // Property and Suite Selection Actions
  addPropertyToSelected: (newSelectedProperties: any[]) => void;
  removePropertyFromSelected: (remainingProperties: any[]) => void;
  addNewSuite: (newSelectedSuites: any[]) => void;
  removeSuite: (newSelectedSuites: any[]) => void;

  // Validation and Computed Properties
  validateContactFormData: () => {
    isValid: boolean;
    errors: Record<string, string>;
  };
  isSubmitDisabled: () => boolean;
}

const defaultContactFormData: IContactFormData = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  type: 'default',
  userRole: 'default',
  Suites: [],
  servicesProvided: [],
  companyName: '',
  address: null,
  sendActivationEmail: false,
  isPrimaryContact: false,
  isDashboardAccess: false,
  properties: [],
  userSuites: [],
  selectedImage: null,
  deleted: false,
  sentEmail: false,
  isSpark: false,
  designated: false,
};

export const useContactFormData = (): UseContactFormDataReturn => {
  const [contactFormState, setContactFormState] =
    useState<UseContactFormDataState>({
      contactFormData: defaultContactFormData,
      isLoading: false,
      errors: {},
      location: '',
      predictions: [],
    });

  const initializeContactFormDataFromEdit = (
    editData: Partial<IContactFormData>
  ) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      contactFormData: {
        ...defaultContactFormData,
        ...editData,
      },
      errors: {},
    }));
  };

  const setContactFormData = (data: Partial<IContactFormData>) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      contactFormData: { ...prev.contactFormData, ...data },
    }));
  };

  const updateContactFormData = (updates: Partial<IContactFormData>) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      contactFormData: { ...prev.contactFormData, ...updates },
    }));
  };

  const clearContactFormData = () => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      contactFormData: defaultContactFormData,
      errors: {},
    }));
  };

  const setErrors = (newErrors: Record<string, string>) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      errors: newErrors,
    }));
  };

  const clearError = (field: string) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: '',
      },
    }));
  };

  const setLoading = (loading: boolean) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      isLoading: loading,
    }));
  };

  const setLocation = (location: string) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      location,
    }));
  };

  const setPredictions = (
    predictions: Array<{ description: string; [key: string]: any }>
  ) => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      predictions,
    }));
  };

  const getLocation = async (value: string): Promise<void> => {
    setLocation(value);

    // Guard clause: Don't make API call for empty values.
    if (!value.trim()) {
      setPredictions([]);
      return;
    }

    try {
      const response: any = await getLocations(value);

      if (response.status !== 200) {
        console.error('Cannot find this address');
        setPredictions([]);
        return;
      }

      const responseJson: any = await response.json();
      const data: any = JSON.parse(responseJson);
      setPredictions(data.predictions || []);
    } catch (error) {
      console.error('Error fetching location predictions:', error);
      setPredictions([]);
    }
  };

  const clearLocationSearch = () => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      location: '',
      predictions: [],
    }));
  };

  const parseAddressComponents = (addressComponents: any[]) => {
    let addressLine1: string | undefined;
    let city: string | undefined;
    let province: string | undefined;
    let postalCode: string | undefined;

    if (addressComponents && addressComponents.length > 0) {
      addressComponents.forEach((component: any) => {
        const types = component.types;

        if (types.includes('street_number')) {
          addressLine1 = component.long_name;
        }

        if (types.includes('route')) {
          addressLine1 = addressLine1
            ? `${addressLine1}, ${component.long_name}`
            : component.long_name;
        }

        if (
          types.includes('neighborhood') ||
          types.includes('sublocality_level_2') ||
          types.includes('sublocality_level_1')
        ) {
          addressLine1 = addressLine1
            ? `${addressLine1}, ${component.long_name}`
            : component.long_name;
        }

        if (types.includes('locality')) {
          city = component.long_name;
        }

        if (types.includes('administrative_area_level_1')) {
          province = component.long_name;
        }

        if (types.includes('postal_code')) {
          postalCode = component.long_name;
        }
      });
    }

    return {
      addressLine1,
      city,
      province,
      postalCode,
    };
  };

  // Handles selection of an address from predictions.
  const onClickLocation = async (address: string): Promise<void> => {
    const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
    const apiurl = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&address=${encodeURIComponent(address)}`;

    try {
      // Get latitude and longitude for the address.
      const result: any = await fetch(apiurl);
      const json = await result.json();

      if (!json.results || json.results.length === 0) {
        console.error('No results found for this address');
        return;
      }

      const location = json.results[0].geometry.location;
      const latitude: number = location.lat;
      const longitude: number = location.lng;

      // Use reverse geocoding to get detailed address components.
      const reverseGeocodeUrl = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&latlng=${latitude},${longitude}`;

      const reverseResult: any = await fetch(reverseGeocodeUrl);
      const addressObj: any = await reverseResult.json();

      if (!addressObj.results || addressObj.results.length === 0) {
        console.error('Cannot find detailed address information');
        return;
      }

      // Parse address components from the API response.
      const addressComponents = addressObj.results[0].address_components;
      const parsedAddress: any = parseAddressComponents(addressComponents);

      // Update contact form data with the selected address.
      setContactFormData({
        address: parsedAddress,
      });

      // Clear the location search
      clearLocationSearch();
    } catch (error) {
      console.error('Unexpected error in onClickLocation:', error);
    }
  };

  // Todo: The guts of this function is still essentially BOS logic and can be refined.
  const addPropertyToSelected = (newSelectedProperties: any[]): void => {
    const propertyDetails: any = newSelectedProperties.map((prop: any) => ({
      propertyId: prop.Id || prop.id,
      name: prop.name,
    }));

    setContactFormData({
      properties: propertyDetails,
      propertyId: undefined,
      suiteId: undefined,
    });

    // Clear any previous property/suite validation errors
    setErrors({
      ...contactFormState.errors,
      type: '',
      suiteId: '',
    });
  };

  const removePropertyFromSelected = (remainingProperties: any[]): void => {
    // Determine which properties are still selected
    const propertyDetails: any = remainingProperties.map((prop: any) => ({
      propertyId: prop.Id || prop.id,
      name: prop.name,
    }));

    setContactFormData({
      properties: propertyDetails,
    });

    // Clear any previous property/suite validation errors.
    setErrors({
      ...contactFormState.errors,
      type: '',
      suiteId: '',
    });
  };

  const filterAndSetSuites = (newSelectedSuites: any[]): void => {
    // Optimization: Create map for O(1) property name lookup.
    const selectedPropertiesMap = new Map(
      contactFormState.contactFormData.properties.map((prop: any) => [
        prop.propertyId,
        prop.name,
      ])
    );

    const filteredSuitesForForm: SuiteListItemContactForm[] = [];
    const filteredUserSuites: IUserSuite[] = [];

    newSelectedSuites.forEach((suite: any) => {
      const propertyId = suite.propertyId;
      const propertyName = selectedPropertiesMap.get(propertyId);

      if (propertyName) {
        filteredSuitesForForm.push({
          Id: suite.Id,
          name: suite.name,
          propertyId: propertyId,
        });

        filteredUserSuites.push({
          propertyId: propertyId,
          propertyName: propertyName,
          suiteId: suite.Id,
          suiteName: suite.name,
        } as IUserSuite);
      }
    });

    setContactFormData({
      Suites: filteredSuitesForForm,
      userSuites: filteredUserSuites,
    });
  };

  const addSelectedSuiteFromSuitesList = (newSelectedSuites: any[]): void => {
    filterAndSetSuites(newSelectedSuites);
  };

  const removeSelectedSuiteFromSuitesList = (
    newSelectedSuites: any[]
  ): void => {
    filterAndSetSuites(newSelectedSuites);
  };

  const validateContactFormData = (): any => {
    const validationErrors: Record<string, string> = {};

    const { contactFormData }: UseContactFormDataState = contactFormState;

    // First Name Validation.
    const trimmedFirstName: string = contactFormData.firstname.trim();

    if (!trimmedFirstName) {
      validationErrors.firstname = ERROR_MESSAGES.firstname.required;
    } else if (!VALIDATION_RULES.name.test(trimmedFirstName)) {
      validationErrors.firstname = ERROR_MESSAGES.firstname.invalid;
    }

    // Last Name Validation.
    const trimmedLastName: string = contactFormData.lastname.trim();

    if (!trimmedLastName) {
      validationErrors.lastname = ERROR_MESSAGES.lastname.required;
    } else if (!VALIDATION_RULES.name.test(trimmedLastName)) {
      validationErrors.lastname = ERROR_MESSAGES.lastname.invalid;
    }

    // Email Validation.
    const trimmedEmail: string = contactFormData.email.trim();

    if (!trimmedEmail) {
      validationErrors.email = ERROR_MESSAGES.email.required;
    } else if (!VALIDATION_RULES.email.test(trimmedEmail)) {
      validationErrors.email = ERROR_MESSAGES.email.invalid;
    }

    // Phone Validation.
    const trimmedPhone: string = contactFormData.phone.trim();

    if (trimmedPhone.length < VALIDATION_RULES.phone.minLength) {
      validationErrors.phone = ERROR_MESSAGES.phone.tooShort;
    } else if (trimmedPhone.length > VALIDATION_RULES.phone.maxLength) {
      validationErrors.phone = ERROR_MESSAGES.phone.tooLong;
    }

    // User Role Validation.
    if (contactFormData.userRole === 'default') {
      validationErrors.userRole = ERROR_MESSAGES.userRole.required;
    }

    // Role-Specific Validations.
    const {
      userRole,
      properties,
      Suites,
      propertyId,
      suiteId,
      servicesProvided,
      companyName,
    } = contactFormData;

    // Home Owner and Realtor validation.
    if (
      (userRole === 'Home Owner' || userRole === 'Realtor') &&
      (!properties?.length || !Suites?.length)
    ) {
      validationErrors.type = `Please select a property and suite for ${userRole}`;
    }

    // Occupant validation.
    if (userRole === 'Occupant' && (!propertyId || !suiteId)) {
      validationErrors.type = 'Please select a property and suite for Occupant';
    }

    // Service Provider validation.
    if (userRole === 'Service Provider' && !servicesProvided?.length) {
      validationErrors.userRole =
        ERROR_MESSAGES.serviceProvider.servicesRequired;
    }

    if (userRole === 'Service Provider' && !companyName) {
      validationErrors.companyName =
        ERROR_MESSAGES.serviceProvider.companyNameRequired;
    }

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors,
    };
  };

  const clearSuites = () => {
    setContactFormState((prev: UseContactFormDataState) => ({
      ...prev,
      contactFormData: {
        ...prev.contactFormData,
        Suites: [], // Clear multi-select suites (Home Owner, Realtor).
        suiteId: undefined, // Clear single suite selection (Occupant, Service Manager).
      },
      errors: {
        ...prev.errors,
        type: '',
        suiteId: '',
      },
    }));
  };

  const isSubmitDisabled = (): boolean => {
    const {
      firstname,
      lastname,
      email,
      phone,
      userRole,
      propertyId,
      suiteId,
      servicesProvided,
      companyName,
      properties,
      Suites,
    } = contactFormState.contactFormData;

    // Basic fields required for all roles.
    const basicFieldsValid: boolean =
      firstname && lastname && email && phone && userRole !== 'default';

    if (!basicFieldsValid) return true;

    // Role-specific validation.
    switch (userRole) {
      case 'Home Owner':
      case 'Realtor':
        return !properties?.length || !Suites?.length;

      case 'Occupant':
        return !propertyId || !suiteId;

      case 'Service Provider':
        return !companyName || !servicesProvided?.length;

      case 'Vendor':
        return servicesProvided?.length === 0;

      case 'Service Manager':
      case 'Property Developer':
      case 'Strata Manager':
      default:
        return false;
    }
  };

  const clearSuitesInFormForRemovedProperty = (
    selectedPropertyToRemove: any
  ) => {
    const propertyIdToRemove =
      selectedPropertyToRemove.Id ||
      selectedPropertyToRemove.id ||
      selectedPropertyToRemove.propertyId;

    const currentFormData: IContactFormData = contactFormState.contactFormData;

    const filteredSuites = currentFormData.Suites.filter((suite) => {
      return suite.propertyId !== propertyIdToRemove;
    });

    const isIUserSuite = (
      item: SuiteListItemContactForm | IUserSuite
    ): item is IUserSuite => {
      return 'suiteName' in item && 'propertyName' in item;
    };

    let filteredUserSuites: IUserSuite[] = null;

    if (currentFormData.userSuites) {
      // TODO: Fix TEMPORARY HACK: The suites data model is inconsistent between the 'contacts' on the API.
      filteredUserSuites = currentFormData.userSuites
        .filter(isIUserSuite)
        .filter((userSuite: IUserSuite) => userSuite.propertyId !== propertyIdToRemove);
    }

    const updatedSuiteId =
      currentFormData.propertyId === propertyIdToRemove
        ? undefined
        : currentFormData.suiteId;

    const updatedPropertyId =
      currentFormData.propertyId === propertyIdToRemove
        ? undefined
        : currentFormData.propertyId;

    const clearedErrors = {
      ...contactFormState.errors,
      type: '',
      suiteId: '',
    };

    const updatedFormData = {
      ...currentFormData,
      Suites: filteredSuites,
      userSuites: filteredUserSuites,
      suiteId: updatedSuiteId,
      propertyId: updatedPropertyId,
    };

    const updatedState = {
      ...contactFormState,
      contactFormData: updatedFormData,
      errors: clearedErrors,
    };

    setContactFormState(updatedState);
  };

  return {
    // State.
    contactFormData: contactFormState.contactFormData,
    isLoading: contactFormState.isLoading,
    errors: contactFormState.errors,
    location: contactFormState.location,
    predictions: contactFormState.predictions,

    // Actions.
    setContactFormData,
    updateContactFormData,
    clearContactFormData,
    setErrors,
    clearError,
    setLoading,
    clearSuites,
    initializeContactFormDataFromEdit,
    clearSuitesInFormForRemovedProperty,

    // Location search actions
    setLocation,
    setPredictions,
    clearLocationSearch,
    getLocation,
    onClickLocation,

    // Property and suite selection actions
    addPropertyToSelected,
    removePropertyFromSelected,
    addNewSuite: addSelectedSuiteFromSuitesList,
    removeSuite: removeSelectedSuiteFromSuitesList,

    // Computed properties and methods.
    validateContactFormData,
    isSubmitDisabled,
  };
};
